import { ProgressSpinner } from "primereact/progressspinner";
import {
  genElDate,
  genElNumber,
  genElObject,
  genElText,
  genElTime,
} from "../../../../components/utils/HeaderElementGenerator";

// TopRows: the variant's rows with the tanggal / jam, the vehicle and the trailer field(s)
// lockVehicleOnKembali: the vehicle can only be changed on the "berangkat" page
const EditorHeaderCard = (props) => {
  const {
    headerData,
    setHeaderData,
    headerError,
    permittedToEdit,
    isBerangkat,
    loading,
    TopRows,
    lockVehicleOnKembali,
    handleOnClickKendaraan,
    handleOnClickPemakai,
    handleOnClickSupir,
    handleOnClickSupirPulang,
    handleOnClickTujuan,
  } = props;

  // UI section

  /**
   * Generate Header Element
   *
   * various function to generate
   * genElDateRange({ field, name, data, setData, primeFormat, disabled, autoFocus })
   * genElObject({ name, field, value, onClick, onClear, disabled, autoFocus, width })
   */

  const headerElTanggal = genElDate({
    field: "tanggal_transaksi",
    name: "Tanggal",
    primeFormat: "dd MM yy",
    data: headerData,
    setData: setHeaderData,
    error: headerError,
    disabled: !permittedToEdit,
  });

  const headerElTanggalBerangkat = genElDate({
    field: "tanggal_berangkat",
    name: "Tanggal Berangkat",
    primeFormat: "dd MM yy ",
    showTime: true,
    data: headerData,
    disabled: true,
    setData: setHeaderData,
    error: headerError,
  });

  const headerElTanggalKembali = genElDate({
    field: "tanggal_pulang",
    name: "Tanggal Kembali",
    primeFormat: "dd MM yy ",
    showTime: true,
    data: headerData,
    disabled: true,
    setData: setHeaderData,
    error: headerError,
  });

  const headerElJam = genElTime({
    field: "tanggal_transaksi_jam",
    name: "Jam",
    data: headerData,
    setData: setHeaderData,
    // full width when it sits under the tanggal on a narrow screen
    width: "w-full sm:w-[6.5rem]",
    error: headerError,
    disabled: !permittedToEdit,
  });

  const headerElVehicle = genElObject({
    name: "Kendaraan",
    field: "data_kendaraan",
    value: headerData.data_kendaraan
      ? `${headerData.data_kendaraan.nomor_polisi} | ${headerData.data_kendaraan.jenis_mobil}`
      : "",
    onClick: handleOnClickKendaraan,
    error: headerError,
    disabled: !permittedToEdit || (lockVehicleOnKembali && !isBerangkat),
  });

  const headerElPemakai = genElObject({
    name: "Pemakai",
    field: "data_pemakai",
    value: headerData.data_pemakai ? headerData.data_pemakai.nama : "",
    onClick: handleOnClickPemakai,
    error: headerError,
    disabled: !permittedToEdit,
  });

  const headerElSupir = genElObject({
    name: "Supir Berangkat",
    field: "data_supir",
    value: headerData.data_supir ? headerData.data_supir.nama : "",
    onClick: handleOnClickSupir,
    error: headerError,
    disabled: !permittedToEdit,
  });

  const headerElSupirPulang = genElObject({
    name: "Supir Pulang",
    field: "data_supir_pulang",
    value: headerData.data_supir_pulang
      ? headerData.data_supir_pulang.nama
      : "",
    onClick: handleOnClickSupirPulang,
    error: headerError,
    disabled: !permittedToEdit,
  });

  const headerElTujuan = genElObject({
    name: "Tujuan",
    field: "data_tujuan",
    value: headerData.data_tujuan ? headerData.data_tujuan.nama : "",
    onClick: handleOnClickTujuan,
    error: headerError,
    disabled: !permittedToEdit,
  });

  const headerElJumlahPenumpang = genElNumber({
    name: "Jumlah Penumpang",
    field: "jumlah_orang",
    value: headerData.jumlah_orang ?? 0,
    showButtons: true,
    emptyWhenZero: true,
    data: headerData,
    setData: setHeaderData,
    error: headerError,
    disabled: !permittedToEdit,
  });

  const headerElSpeedoBerangkat = genElNumber({
    name: "Speedometer Berangkat",
    field: "speedo_mulai",
    value: headerData.speedo_mulai ?? 0,
    showButtons: true,
    emptyWhenZero: true,
    // 0 is not a valid speedometer (no pengecekan is made without it), so the v stops at 1
    min: 1,
    data: headerData,
    setData: setHeaderData,
    error: headerError,
    disabled: !permittedToEdit || !isBerangkat,
  });

  const headerElSpeedoKembali = genElNumber({
    name: "Speedometer Kembali",
    field: "speedo_akhir",
    value: headerData.speedo_akhir ?? 0,
    showButtons: true,
    emptyWhenZero: true,
    min: 1,
    data: headerData,
    setData: setHeaderData,
    error: headerError,
    disabled: !permittedToEdit || isBerangkat,
  });

  const headerElKeterangan = genElText({
    name: "Keterangan",
    field: "keterangan",
    value: headerData.keterangan ?? "",
    data: headerData,
    setData: setHeaderData,
    error: headerError,
    disabled: !permittedToEdit,
  });

  return (
    <div className="elevated-card-tight-vertical !p-0 w-full col-start-start relative wrap gap-3">
      <div className="col-start-start gap-3 w-full px-6 py-4">
        <TopRows
          {...props}
          headerElTanggal={headerElTanggal}
          headerElJam={headerElJam}
          headerElVehicle={headerElVehicle}
        />
        <div className="row-start-center w-full gap-3 flex-wrap sm:flex-nowrap">
          {headerElPemakai}
          {headerElTujuan}
        </div>
        <div className="row-start-center w-full gap-3 flex-wrap sm:flex-nowrap">
          {headerElSupir}
          {headerElSupirPulang}
          {headerElJumlahPenumpang}
        </div>
        <div className="row-start-center w-full gap-3 flex-wrap sm:flex-nowrap">
          {headerElSpeedoBerangkat}
          {headerElSpeedoKembali}
        </div>
        <div className="row-start-center w-full gap-3 flex-wrap sm:flex-nowrap">
          {headerElTanggalBerangkat}
          {headerElTanggalKembali}
        </div>
        <div className="row-start-center w-full gap-3 flex-wrap sm:flex-nowrap">
          {headerElKeterangan}
        </div>
      </div>
      {loading && (
        <div
          className={`col-center-center w-100  gap-2 absolute z-100 h-100 bg-black/20 rounded `}
        >
          <ProgressSpinner />
        </div>
      )}
    </div>
  );
};

export default EditorHeaderCard;
